import React, { useState, useEffect, useRef } from "react";
import { Chessboard } from "react-chessboard";
import { Chess } from 'chess.js';
import { useTranslation } from 'react-i18next';
import { HEADER_HEIGHT } from '../config/Constants';
import PuzzleService from "../services/PuzzleService";
import GameUtils from '../utils/gameUtils';

import { View, Text } from 'react-native';

// Puzzle of the Day. The player is always the side to move and must find the
// forced mate. Each attacker move is confirmed server-side (the solution is
// never sent to the browser); on a correct move the forced defender reply is
// played automatically. The Sumo push mechanic and check rules are handled by
// GameUtils (the shared move engine used by live games).
export default function Puzzle() {
  const { t } = useTranslation();

  const [windowDimensions, setWindowDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const [puzzle, setPuzzle] = useState(null);
  const [position, setPosition] = useState(null);   // FEN driving the board
  const [status, setStatus] = useState('loading');  // loading|none|playing|solved|error
  const [message, setMessage] = useState("");
  const [lastArrow, setLastArrow] = useState([]);

  const gameRef = useRef(null);        // authoritative Chess instance
  const lastMoveRef = useRef(null);    // "from,to[,displaced]" for the push-back rule
  const plyRef = useRef(0);            // next attacker index in the solution
  const guRef = useRef(null);          // GameUtils engine
  const busyRef = useRef(false);       // ignore drops while awaiting the server

  useEffect(() => {
    const handleResize = () => {
      setWindowDimensions({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    let cancelled = false;
    PuzzleService.getDaily()
      .then((resp) => {
        if (cancelled) return;
        const pz = resp.data && resp.data.puzzle;
        if (!pz) {
          setStatus('none');
          return;
        }
        gameRef.current = new Chess(pz.fen);
        lastMoveRef.current = pz.last_move || null;
        plyRef.current = 0;
        // The engine flips the side-to-move for us; we set isWhite/myTurn per
        // move (see applyMove), so the constructor values are placeholders.
        guRef.current = new GameUtils(null, pz.side_to_move === 'w', false, true, true);
        setPuzzle(pz);
        setPosition(pz.fen);
        setStatus('playing');
        if (pz.last_move) {
          const [f, tgt] = pz.last_move.split(',');
          setLastArrow([[f, tgt, 'rgba(0,0,0,0.4)']]);
        }
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });
    return () => { cancelled = true; };
  }, []);

  // Apply `move` for `moverColor` to the live board via the Sumo engine.
  // Returns the engine result ({currentFen, lastMoveAsString, hasMovesLeft}) or
  // a falsy/error value if the move is not a legal Sumo move.
  const applyMove = (move, moverColor) => {
    const working = new Chess(gameRef.current.fen());
    guRef.current.isWhite = moverColor === 'w';
    guRef.current.myTurn = true;
    const res = guRef.current.makeAMove(
      move, moverColor, false, working, lastMoveRef.current, () => {}
    );
    if (!res || res.error) return null;
    gameRef.current = new Chess(res.currentFen);
    lastMoveRef.current = res.lastMoveAsString;
    return res;
  };

  const buildMove = (source, target, piece) => {
    const move = { from: source, to: target };
    // Promote pushed/advancing pawns automatically.
    if (piece && piece[1] === 'P' && (target[1] === '8' || target[1] === '1')) {
      move.promotion = 'q';
    }
    return move;
  };

  const onDrop = (source, target, piece) => {
    if (status !== 'playing' || busyRef.current || !puzzle) return false;

    const humanColor = puzzle.side_to_move;
    const oppColor = humanColor === 'w' ? 'b' : 'w';
    const prevFen = gameRef.current.fen();
    const prevLast = lastMoveRef.current;
    const myPly = plyRef.current;

    const res = applyMove(buildMove(source, target, piece), humanColor);
    if (!res) {
      setMessage(t('puzzle_illegal') || "That is not a legal move.");
      return false;
    }

    // Optimistically show the player's move, then confirm with the server.
    setPosition(res.currentFen);
    setLastArrow([[source, target, 'rgba(30,130,255,0.5)']]);
    setMessage("");
    busyRef.current = true;

    PuzzleService.step(puzzle.id, myPly, `${source},${target}`)
      .then((resp) => {
        const data = resp.data || {};
        if (!data.correct) {
          // Revert the wrong move.
          gameRef.current = new Chess(prevFen);
          lastMoveRef.current = prevLast;
          setPosition(prevFen);
          setLastArrow(prevLast ? [[prevLast.split(',')[0], prevLast.split(',')[1], 'rgba(0,0,0,0.4)']] : []);
          setMessage(t('puzzle_wrong') || "Not the solution — try again.");
          busyRef.current = false;
          return;
        }
        if (data.solved) {
          plyRef.current = myPly + 1;
          setStatus('solved');
          setMessage(t('puzzle_solved') || "Solved! Come back tomorrow for a new puzzle.");
          busyRef.current = false;
          return;
        }
        // Correct but not finished: play the forced defender reply.
        const reply = data.reply;
        const [rf, rt] = reply.split(',');
        const oppRes = applyMove({ from: rf, to: rt }, oppColor);
        if (oppRes) {
          setPosition(oppRes.currentFen);
          setLastArrow([[rf, rt, 'rgba(200,60,60,0.55)']]);
        }
        plyRef.current = myPly + 2;
        setMessage(t('puzzle_keep_going') || "Correct! Keep going.");
        busyRef.current = false;
      })
      .catch(() => {
        gameRef.current = new Chess(prevFen);
        lastMoveRef.current = prevLast;
        setPosition(prevFen);
        setMessage(t('puzzle_error') || "Something went wrong. Try again.");
        busyRef.current = false;
      });

    return true;
  };

  const smallWidth = windowDimensions.width <= 480;
  const prct = smallWidth ? 0.92 : 0.8;
  const minSize = Math.min(windowDimensions.width, windowDimensions.height - HEADER_HEIGHT - 120) * prct;

  if (status === 'loading') {
    return (
      <View style={{ alignItems: "center", marginTop: 40 }}>
        <Text style={{ color: '#fff', fontSize: 18 }}>{t('loading') || "Loading…"}</Text>
      </View>
    );
  }

  if (status === 'none' || status === 'error') {
    return (
      <View style={{ alignItems: "center", marginTop: 40 }}>
        <Text style={{ color: '#fff', fontSize: 18, textAlign: 'center', maxWidth: 400 }}>
          {status === 'none'
            ? (t('puzzle_none') || "No puzzle today — check back soon.")
            : (t('puzzle_error') || "Could not load the puzzle.")}
        </Text>
      </View>
    );
  }

  const toMoveLabel = puzzle.side_to_move === 'w'
    ? (t('white_to_move') || "White to move")
    : (t('black_to_move') || "Black to move");
  const goal = (t('puzzle_goal') || "Mate in %d").replace('%d', puzzle.mate_in);

  return (
    <View style={{ alignItems: "center", marginTop: 20 }}>
      <Text style={{ color: '#fff', fontSize: 22, fontWeight: 'bold', marginBottom: 4 }}>
        {t('puzzle_of_the_day') || "Puzzle of the Day"}
      </Text>
      <Text style={{ color: '#f5c542', fontSize: 16, marginBottom: 14 }}>
        {toMoveLabel} · {goal}
      </Text>

      <View style={{ width: minSize + 'px' }}>
        <Chessboard
          id="PuzzleBoard"
          position={position}
          onPieceDrop={onDrop}
          arePiecesDraggable={status === 'playing'}
          customArrows={lastArrow}
          boardOrientation={puzzle.side_to_move === 'w' ? 'white' : 'black'}
          customBoardStyle={{
            boxShadow: "0 5px 25px rgba(0, 0, 0, 0.5)",
            borderRadius: "6px",
          }}
          customLightSquareStyle={{ backgroundColor: "#D1A038" }}
          customDarkSquareStyle={{ backgroundColor: "#A1291F" }}
        />
      </View>

      {message !== "" && (
        <Text style={{
          color: status === 'solved' ? '#5ad469' : '#fff',
          fontSize: 17, marginTop: 18, textAlign: 'center',
          maxWidth: Math.max(minSize, 400),
        }}>
          {message}
        </Text>
      )}
    </View>
  );
}
